// Navbar.js
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import { AuthContext } from "../context/AuthContext";
import AuthDrawer from "./AuthDrawer";
import chefLogo from "../assets/images/chefchattyglobal.png";
import { SettingsContext } from "../context/SettingsContext";
import SettingsOverlay from "./SettingsOverlay"; 
import { ModalContext } from "../context/ModalContext"; // Import the ModalContext

function Navbar() {
  const { firstName, lastName } = useContext(SettingsContext);
  const navigate = useNavigate();

  const {
    user,
    logout,
    login,
    signup,
    onRequestPasswordReset,
    onResendConfirmation,
  } = useContext(AuthContext);

  // We can invoke openConfirmModal from our ModalContext
  const { openConfirmModal } = useContext(ModalContext);

  // AuthDrawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Avatar menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Settings overlay state
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMyRecipes = () => {
    // Could open in new tab if desired
    navigate("/saved-recipes");
    handleAvatarMenuClose();
  };

  const handleSettings = () => {
    setSettingsOpen(true);
    handleAvatarMenuClose();
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  // Instead of calling `logout()` directly, we open a confirm modal
  const handleLogoutClick = () => {
    handleAvatarMenuClose();
    openConfirmModal({
      title: "Confirm Logout",
      message: "Are you sure you want to log out?",
      onConfirm: () => {
        // If user confirms
        logout();
        navigate("/");
      },
      // If you'd like to do anything else on close, add `onClose` here
    });
  };

  // Helper function to get user initials
  const getUserInitials = () => {
    if (!firstName && !lastName) {
      return "?";
    }
    let initials = "";
    if (firstName?.length > 0) {
      initials += firstName.charAt(0).toUpperCase();
    }
    if (lastName?.length > 0) {
      initials += lastName.charAt(0).toUpperCase();
    }
    return initials || "?";
  };

  return (
    <>
      <AppBar position="sticky">
        <Toolbar>
          <img
            src={chefLogo}
            alt="Chef Chatty"
            style={{ height: "60px", cursor: "pointer", marginRight: 8 }}
            onClick={() => navigate("/meal-planner")}
          />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Montserrat Alternates",
              fontSize: 24,
              color: "#d7a669",
              flexGrow: 1,
            }}
          >
            Chef Chatty
          </Typography>

          {/* If user is not logged in, show "Login / Sign Up" button */}
          {!user ? (
            <Button color="inherit" onClick={() => setDrawerOpen(true)}>
              Login / Sign Up
            </Button>
          ) : (
            // If user is logged in, show avatar which opens a menu
            <IconButton onClick={handleAvatarClick} color="inherit">
              <Avatar sx={{ bgcolor: "#d7a669", color: "#000" }}>
                {getUserInitials()}
              </Avatar>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* AuthDrawer for login/signup */}
      <AuthDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onLogin={login}
        onSignup={signup}
        onRequestPasswordReset={onRequestPasswordReset}
        onResendConfirmation={onResendConfirmation}
      />

      {/* Avatar Menu (My Recipes, Settings, Logout) */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleAvatarMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleMyRecipes}>My Recipes</MenuItem>
        <MenuItem onClick={handleSettings}>Settings</MenuItem>
        <MenuItem onClick={handleLogoutClick}>Logout</MenuItem>
      </Menu>

      {/* The overlay for user settings with side nav (Profile, Preferences, Security) */}
      <SettingsOverlay
        open={settingsOpen}
        onClose={handleSettingsClose}
      />
    </>
  );
}

export default Navbar;
